import { useState, useEffect, useMemo, useCallback } from 'react';
import { format, subDays, subWeeks, subMonths, isAfter } from 'date-fns';
import {
  AlertCircle, Clock, TrendingUp, Mail, Megaphone, ShoppingBag, Plane,
  Heart, Users, Calendar, ChevronRight, X, User, Zap, Loader2, Star,
  Reply, Archive, ListPlus, Send
} from 'lucide-react';
import { getBriefing, batchUpdate, updateEmail, createTodo, getEmailBody } from '../../services/emailApi';

function senderLabel(from) {
  if (!from) return 'Unknown';
  const match = from.match(/^(.+?)\s*<.+>$/);
  return match ? match[1].replace(/^"|"$/g, '') : from;
}

function getCutoffDate(period) {
  const now = new Date();
  if (period === 'daily') return subDays(now, 1);
  if (period === 'weekly') return subWeeks(now, 1);
  return subMonths(now, 1);
}

function periodLabel(period) {
  if (period === 'daily') return 'Today';
  if (period === 'weekly') return 'This Week';
  return 'This Month';
}

function buildSections(emails) {
  const actions = emails.filter(e => e.llm_action_required);
  const finance = emails.filter(e => e.llm_category === 'Finance' && !e.llm_action_required);
  const work = emails.filter(e => (e.llm_category === 'Work' || e.llm_category === 'Updates') && !e.llm_action_required);
  const travel = emails.filter(e => e.llm_category === 'Travel' && !e.llm_action_required);
  const health = emails.filter(e => e.llm_category === 'Health' && !e.llm_action_required);
  const social = emails.filter(e => e.llm_category === 'Social' && !e.llm_action_required);
  const shopping = emails.filter(e => e.llm_category === 'Shopping' && !e.llm_action_required);
  const newsletters = emails.filter(e => e.llm_category === 'Newsletters' && !e.llm_action_required);

  const sections = [];

  if (actions.length > 0) sections.push({ key: 'actions', title: 'Actions Required', icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50', emails: actions });
  if (work.length > 0) sections.push({ key: 'work', title: 'Work & Updates', icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50', emails: work });
  if (finance.length > 0) sections.push({ key: 'finance', title: 'Financial Updates', icon: Mail, color: 'text-green-600', bg: 'bg-green-50', emails: finance });
  if (travel.length > 0) sections.push({ key: 'travel', title: 'Travel', icon: Plane, color: 'text-cyan-600', bg: 'bg-cyan-50', emails: travel });
  if (health.length > 0) sections.push({ key: 'health', title: 'Health & Wellness', icon: Heart, color: 'text-pink-600', bg: 'bg-pink-50', emails: health });
  if (social.length > 0) sections.push({ key: 'social', title: 'Social & Networking', icon: Users, color: 'text-violet-600', bg: 'bg-violet-50', emails: social });
  if (shopping.length > 0) sections.push({ key: 'shopping', title: 'Shopping & Orders', icon: ShoppingBag, color: 'text-amber-600', bg: 'bg-amber-50', emails: shopping });
  if (newsletters.length > 0) sections.push({ key: 'newsletters', title: 'Newsletters & Reading', icon: Megaphone, color: 'text-slate-600', bg: 'bg-slate-50', emails: newsletters });

  return sections;
}

function buildSectionSummary(emails) {
  const summaries = emails
    .map(e => e.llm_summary)
    .filter(s => !!s && s.length > 0);
  if (summaries.length === 0) return '';
  if (summaries.length === 1) return summaries[0];
  const senders = [...new Set(emails.map(e => senderLabel(e.from_address)))];
  const actionEmails = emails.filter(e => e.llm_action_required && e.llm_action);
  const parts = [];
  if (actionEmails.length > 0) {
    const actionBits = actionEmails
      .slice(0, 3)
      .map(e => `${e.llm_action} (${senderLabel(e.from_address)})`)
      .join('; ');
    parts.push(`Action needed: ${actionBits}${actionEmails.length > 3 ? ` and ${actionEmails.length - 3} more` : ''}.`);
  }
  const nonAction = summaries.slice(0, 5);
  parts.push(nonAction.join('. ').replace(/\.\./g, '.'));
  if (summaries.length > 5) {
    parts.push(`Plus ${summaries.length - 5} more from ${senders.length} sender${senders.length !== 1 ? 's' : ''}.`);
  }
  return parts.join(' ');
}

function BriefingEmailItem({ email, onClick }) {
  return (
    <button
      className="flex items-start gap-3 py-3 sm:py-2.5 px-2 sm:px-1 w-full text-left hover:bg-gray-50 rounded-lg transition-colors cursor-pointer min-h-[48px]"
      onClick={onClick}
    >
      <ChevronRight className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 leading-snug truncate">{email.subject}</p>
        <div className="flex items-center gap-2 mt-1 sm:mt-0.5">
          <span className="text-xs text-gray-500 truncate">{senderLabel(email.from_address)}</span>
          {email.llm_action && (
            <span className="text-xs text-red-600 font-medium flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              <span className="hidden sm:inline">{email.llm_action}</span>
              <span className="sm:hidden truncate max-w-[120px]">{email.llm_action}</span>
            </span>
          )}
        </div>
        {email.llm_time_estimate && (
          <span className="inline-flex items-center gap-1 text-[11px] sm:text-[10px] text-gray-400 mt-1">
            <Clock className="w-3 h-3 sm:w-2.5 sm:h-2.5" />
            {email.llm_time_estimate}
          </span>
        )}
      </div>
    </button>
  );
}

function BriefingSectionCard({ section, onEmailClick, onArchiveAll, isArchiving }) {
  const Icon = section.icon;
  const summary = useMemo(() => buildSectionSummary(section.emails), [section.emails]);
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 px-4 sm:px-5 py-3.5 sm:py-3 border-b border-gray-100">
        <div className={`w-9 h-9 sm:w-8 sm:h-8 ${section.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${section.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">{section.title}</h3>
          <p className="text-xs text-gray-500">{section.emails.length} email{section.emails.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
          onClick={(e) => { e.stopPropagation(); onArchiveAll(section.emails.map(em => em.id)); }}
          disabled={isArchiving}
        >
          {isArchiving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
          <span className="hidden sm:inline">Archive All</span>
          <span className="sm:hidden">Archive</span>
        </button>
      </div>
      {summary && (
        <div className="px-4 sm:px-5 py-3 border-b border-gray-100 bg-gray-50/50">
          <p className="text-sm text-gray-600 leading-relaxed">{summary}</p>
        </div>
      )}
      <div className="px-3 sm:px-4 py-1 divide-y divide-gray-100">
        {section.emails.slice(0, 8).map(email => (
          <BriefingEmailItem key={email.id} email={email} onClick={() => onEmailClick(email)} />
        ))}
        {section.emails.length > 8 && (
          <p className="text-xs text-gray-400 py-2 text-center">
            +{section.emails.length - 8} more
          </p>
        )}
      </div>
    </div>
  );
}

function EmailDetailModal({ email, onClose, onEmailUpdated }) {
  const [emailContent, setEmailContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(true);
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [archiving, setArchiving] = useState(false);
  const [starring, setStarring] = useState(false);
  const [addingTodo, setAddingTodo] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setContentLoading(true);
    getEmailBody(email.id)
      .then(data => { if (!cancelled) setEmailContent(data); })
      .catch(() => { if (!cancelled) setEmailContent(null); })
      .finally(() => { if (!cancelled) setContentLoading(false); });
    return () => { cancelled = true; };
  }, [email.id]);

  const handleArchive = useCallback(async () => {
    setArchiving(true);
    try {
      await batchUpdate([email.id], 'archive');
      onEmailUpdated?.();
      onClose();
    } catch {
      setArchiving(false);
    }
  }, [email.id, onClose, onEmailUpdated]);

  const handleStar = useCallback(async () => {
    setStarring(true);
    try {
      await updateEmail(email.id, { is_starred: !email.is_starred });
      onEmailUpdated?.();
    } catch {
      // ignore
    } finally {
      setStarring(false);
    }
  }, [email.id, email.is_starred, onEmailUpdated]);

  const handleAddTodo = useCallback(async () => {
    setAddingTodo(true);
    try {
      await createTodo({
        title: email.llm_action || email.subject,
        email_id: email.id,
        time_estimate: email.llm_time_estimate || undefined,
      });
    } catch {
      // ignore
    } finally {
      setAddingTodo(false);
    }
  }, [email]);

  const bodyHtml = emailContent?.html || emailContent?.body_html;
  const bodyText = emailContent?.text || emailContent?.body_text || emailContent?.body;
  const hasHtml = !!bodyHtml;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(15,23,42,0.45)' }}
      onClick={onClose}
    >
      <div
        className="relative bg-white sm:rounded-2xl shadow-2xl flex flex-col w-full h-full sm:w-auto sm:h-auto"
        style={{ maxWidth: 'min(780px, 96vw)', maxHeight: '100vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 sm:px-6 pt-4 sm:pt-5 pb-3 sm:pb-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold text-gray-900 leading-snug mb-2 pr-6">
                {email.subject}
              </h2>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {senderLabel(email.from_address)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {format(new Date(email.created_at), 'MMM d, yyyy h:mm a')}
                </span>
                {email.llm_category && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-gray-100 rounded-full px-2 py-0.5">
                    {email.llm_category}
                  </span>
                )}
              </div>

              {email.llm_action_required && email.llm_action && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 flex items-start gap-2">
                  <Zap className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-red-700 mb-1">{email.llm_action}</p>
                    <div className="flex items-center gap-2">
                      {email.llm_time_estimate && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">
                          <Clock className="w-2.5 h-2.5" />
                          {email.llm_time_estimate}
                        </span>
                      )}
                      <span className="text-[10px] text-red-400">Action required</span>
                    </div>
                  </div>
                </div>
              )}

              {email.llm_summary && (
                <p className="mt-2 text-xs text-gray-500 bg-gray-100 rounded-md px-3 py-2 italic leading-relaxed">
                  AI: {email.llm_summary}
                </p>
              )}
            </div>
            <button
              className="flex-shrink-0 w-10 h-10 sm:w-7 sm:h-7 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 transition-colors min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0"
              onClick={onClose}
            >
              <X className="w-5 h-5 sm:w-4 sm:h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {contentLoading ? (
            <div className="flex items-center justify-center h-48 gap-3 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Loading email...</span>
            </div>
          ) : !bodyHtml && !bodyText ? (
            <div className="flex flex-col items-center justify-center h-48 gap-2 text-gray-400">
              <Mail className="w-8 h-8 opacity-30" />
              <p className="text-sm">No content available</p>
              {email.body_preview && (
                <p className="text-xs max-w-md text-center px-6 leading-relaxed opacity-70">{email.body_preview}</p>
              )}
            </div>
          ) : hasHtml ? (
            <iframe
              srcDoc={`<base target="_blank">${bodyHtml}`}
              sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              className="w-full border-0"
              style={{ minHeight: 400, height: '100%', display: 'block' }}
              title="Email content"
            />
          ) : (
            <pre className="text-sm text-gray-900 whitespace-pre-wrap break-words leading-relaxed p-6 font-sans">
              {bodyText}
            </pre>
          )}
        </div>

        {/* Reply area */}
        {showReply && (
          <div className="px-4 sm:px-6 py-3 border-t border-gray-200 flex-shrink-0 bg-blue-50/50">
            <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
              <Reply className="w-3.5 h-3.5" />
              <span>Replying to {senderLabel(email.from_address)}</span>
            </div>
            <textarea
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              placeholder="Type your reply..."
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 resize-none bg-white"
              rows={4}
              autoFocus
            />
            <div className="flex items-center justify-end gap-2 mt-2">
              <button
                className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                onClick={() => { setShowReply(false); setReplyText(''); }}
              >
                Cancel
              </button>
              <button
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50"
                onClick={() => {
                  // Reply not yet wired to send endpoint for briefing view
                  setShowReply(false);
                  setReplyText('');
                }}
                disabled={!replyText.trim()}
              >
                <Send className="w-3.5 h-3.5" />
                Send
              </button>
            </div>
          </div>
        )}

        {/* Footer actions */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 pb-[calc(0.75rem+3.75rem)] sm:pb-3 border-t border-gray-200 flex-shrink-0 bg-gray-50 sm:rounded-b-2xl gap-2">
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            {/* Reply - mobile icon */}
            <button
              className="sm:hidden inline-flex items-center justify-center w-11 h-11 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors text-gray-600"
              onClick={() => setShowReply(!showReply)}
            >
              <Reply className="w-4 h-4" />
            </button>
            {/* Reply - desktop */}
            <button
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors text-gray-600"
              onClick={() => setShowReply(!showReply)}
            >
              <Reply className="w-3.5 h-3.5" />
              Reply
            </button>

            {/* Star - mobile icon */}
            <button
              className={`sm:hidden inline-flex items-center justify-center w-11 h-11 border rounded-md transition-colors ${email.is_starred ? 'border-amber-300 text-amber-600 bg-amber-50' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}
              onClick={handleStar}
              disabled={starring}
            >
              {starring ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className={`w-4 h-4 ${email.is_starred ? 'fill-amber-400' : ''}`} />}
            </button>
            {/* Star - desktop */}
            <button
              className={`hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border rounded-md transition-colors ${email.is_starred ? 'border-amber-300 text-amber-600 bg-amber-50' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}
              onClick={handleStar}
              disabled={starring}
            >
              {starring ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Star className={`w-3.5 h-3.5 ${email.is_starred ? 'fill-amber-400' : ''}`} />}
              {email.is_starred ? 'Starred' : 'Star'}
            </button>

            {/* Todo - mobile icon */}
            <button
              className="sm:hidden inline-flex items-center justify-center w-11 h-11 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors text-gray-600 disabled:opacity-50"
              onClick={handleAddTodo}
              disabled={addingTodo}
            >
              {addingTodo ? <Loader2 className="w-4 h-4 animate-spin" /> : <ListPlus className="w-4 h-4" />}
            </button>
            {/* Todo - desktop */}
            <button
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors text-gray-600 disabled:opacity-50"
              onClick={handleAddTodo}
              disabled={addingTodo}
            >
              {addingTodo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ListPlus className="w-3.5 h-3.5" />}
              To-do
            </button>

            {/* Archive - mobile icon */}
            <button
              className="sm:hidden inline-flex items-center justify-center w-11 h-11 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors text-gray-600 disabled:opacity-50"
              onClick={handleArchive}
              disabled={archiving}
            >
              {archiving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
            </button>
            {/* Archive - desktop */}
            <button
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors text-gray-600 disabled:opacity-50"
              onClick={handleArchive}
              disabled={archiving}
            >
              {archiving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
              {archiving ? 'Archiving...' : 'Archive'}
            </button>
          </div>
          <button
            className="text-sm text-gray-400 hover:text-gray-700 transition-colors min-h-[44px] sm:min-h-0 px-2"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BriefingView({ account }) {
  const [period, setPeriod] = useState('weekly');
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [archivingSection, setArchivingSection] = useState(null);

  const fetchBriefing = useCallback(() => {
    setLoading(true);
    getBriefing(period, account === 'all' ? undefined : account)
      .then(data => {
        setEmails(Array.isArray(data) ? data : data.emails || []);
      })
      .catch(() => setEmails([]))
      .finally(() => setLoading(false));
  }, [period, account]);

  useEffect(() => {
    fetchBriefing();
  }, [fetchBriefing]);

  const filteredEmails = useMemo(() => {
    const cutoff = getCutoffDate(period);
    return emails.filter(e => isAfter(new Date(e.created_at), cutoff));
  }, [emails, period]);

  const sections = useMemo(() => buildSections(filteredEmails), [filteredEmails]);

  const stats = useMemo(() => {
    const actionCount = filteredEmails.filter(e => e.llm_action_required).length;
    const categories = new Set(filteredEmails.map(e => e.llm_category).filter(Boolean));
    const senders = new Set(filteredEmails.map(e => senderLabel(e.from_address)));
    const totalTime = filteredEmails.reduce((sum, e) => {
      if (!e.llm_time_estimate) return sum;
      const match = e.llm_time_estimate.match(/(\d+)/);
      if (!match) return sum;
      const val = parseInt(match[1]);
      if (e.llm_time_estimate.includes('hour')) return sum + val * 60;
      return sum + val;
    }, 0);
    return { total: filteredEmails.length, actionCount, categories: categories.size, senders: senders.size, totalTime };
  }, [filteredEmails]);

  const handleArchiveSection = useCallback(async (sectionKey, ids) => {
    setArchivingSection(sectionKey);
    try {
      await batchUpdate(ids, 'archive');
      fetchBriefing();
    } catch {
      // ignore
    } finally {
      setArchivingSection(null);
    }
  }, [fetchBriefing]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-400">
          <Mail className="w-10 h-10 animate-pulse" />
          <p className="text-sm font-medium">Building your briefing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header with period selector */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {periodLabel(period)} Briefing
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {format(getCutoffDate(period), 'MMM d')} &mdash; {format(new Date(), 'MMM d, yyyy')}
            </p>
          </div>
          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1 shadow-sm">
            {['daily', 'weekly', 'monthly'].map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-4 py-2.5 sm:px-3 sm:py-1.5 rounded-md text-sm sm:text-xs font-medium transition-colors min-h-[44px] sm:min-h-0 ${
                  period === p
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                {p === 'daily' ? 'Daily' : p === 'weekly' ? 'Weekly' : 'Monthly'}
              </button>
            ))}
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-4 sm:py-3 shadow-sm">
            <p className="text-xs sm:text-[10px] text-gray-500 uppercase tracking-wider">Emails</p>
            <p className="text-2xl sm:text-lg font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-4 sm:py-3 shadow-sm">
            <p className="text-xs sm:text-[10px] text-gray-500 uppercase tracking-wider">Need Action</p>
            <p className="text-2xl sm:text-lg font-bold text-red-600">{stats.actionCount}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-4 sm:py-3 shadow-sm">
            <p className="text-xs sm:text-[10px] text-gray-500 uppercase tracking-wider">Categories</p>
            <p className="text-2xl sm:text-lg font-bold text-gray-900">{stats.categories}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-4 sm:py-3 shadow-sm">
            <p className="text-xs sm:text-[10px] text-gray-500 uppercase tracking-wider">Est. Time</p>
            <p className="text-2xl sm:text-lg font-bold text-gray-900">
              {stats.totalTime > 60 ? `${Math.round(stats.totalTime / 60)}h ${stats.totalTime % 60}m` : stats.totalTime > 0 ? `${stats.totalTime}m` : '--'}
            </p>
          </div>
        </div>

        {/* Sections or empty state */}
        {sections.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3 text-gray-400">
            <Calendar className="w-12 h-12 opacity-20" />
            <p className="text-sm font-medium">No emails in this period</p>
            <p className="text-xs">Try switching to a longer time range.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {sections.map(section => (
              <BriefingSectionCard
                key={section.key}
                section={section}
                onEmailClick={setSelectedEmail}
                onArchiveAll={(ids) => handleArchiveSection(section.key, ids)}
                isArchiving={archivingSection === section.key}
              />
            ))}
          </div>
        )}
      </div>

      {selectedEmail && (
        <EmailDetailModal
          email={selectedEmail}
          onClose={() => setSelectedEmail(null)}
          onEmailUpdated={fetchBriefing}
        />
      )}
    </div>
  );
}
